import type * as React from 'react'
import { forwardRef } from 'react'
import type { JSX } from 'react'

import { clsx } from 'clsx'

import { Flex, Skeleton, SkeletonText } from '@buffer-mono/popcorn'

import { PostCardAddons } from '~publish/components/PostCardAddons'
import { PostCardContent } from '~publish/components/PostCardContent'
import { PostNotesEntryPoint } from '~publish/components/PostNotesEntryPoint'
import { PostCardTags } from '~publish/components/PostCardTags'
import { readFragment } from '~publish/graphql'
import type { FragmentOf } from '~publish/graphql'
import { useDateTimeFormatter } from '~publish/hooks/useDateTimeFormatter'
import { useCollaborationNotesPanel } from '~publish/components/PostDialogs/PostDetailsContent/useCollaborationNotesPanel'
import { ensureUrlProtocol } from '~publish/legacy/composer/composer/utils/StringUtils'

import { PostCardAttachments } from './PostCardAttachments'
import {
  PostCardContextProvider,
  useMetadataTitle,
  usePostData,
} from './PostCardContext'
import { PostCard_Post } from './fragments/PostCard_Post'
import type { PostCard_PostData } from './fragments/PostCard_Post'
import { PostCardDisconnectedChannelNotice } from './PostCardDisconnectedChannelNotice'
import { PostCardErrorNotice } from './PostCardErrorNotice'
import { PostCardMetrics } from './PostCardMetrics'
import { PostCardNotes } from './PostCardNotes'
import { PostCardContent_Annotation } from '../PostCardContent/PostCardContent'
import { PostCardFooter } from '../PostCardFooter'
import type { PostCardFooterLayout } from '../PostCardFooter'
import { PostCardHeader } from '../PostCardHeader'
import { PostCardPausedQueueNotice } from './PostCardPausedQueueNotice'
import { PostCardProcessingNotice } from './PostCardProcessingNotice'
import { PostCardRecurrenceNotice } from './PostCardRecurrenceNotice'
import { PostMediaBentoBoxPreview, PostMediaRowPreview } from './PostMedia'
import { RefreshConnection } from '../PostDetails/PostMetrics'

import styles from './PostCard.module.css'

export { PostCard_Post, type PostCard_PostData }

export const NOTES_LIMIT = 35

const readablePostStatus: Record<PostCard_PostData['status'], string> = {
  draft: 'draft',
  needs_approval: 'needs approval',
  sent: 'sent',
  error: 'errored',
  scheduled: 'scheduled',
  sending: 'being sent',
} as const

export const PostCardProvider = ({
  post,
  children,
}: {
  post: FragmentOf<typeof PostCard_Post>
  children: React.ReactNode
}): JSX.Element => {
  const postData = readFragment(PostCard_Post, post)
  return (
    <PostCardContextProvider post={postData}>
      {children}
    </PostCardContextProvider>
  )
}

export const PostCardContainer = forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { className?: string }
>(({ className, ...props }, ref): JSX.Element => {
  const post = usePostData()
  const dateTimeFormatter = useDateTimeFormatter()

  return (
    <article
      {...props}
      data-post-id={post.id}
      data-testid="post-card"
      className={clsx(
        styles.cardContainer,
        post.isProcessing && styles.processing,
        className,
      )}
      ref={ref}
      aria-label={`Post ${readablePostStatus[post.status]} for ${
        post.channel.name
      } ${post.channel.service} channel ${
        post.dueAt ? `on ${dateTimeFormatter(post.dueAt)}` : ''
      }`}
    >
      {props.children}
    </article>
  )
})
PostCardContainer.displayName = 'PostCardContainer'

export const PostCardFrame = forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { className?: string }
>((props, ref): JSX.Element => {
  return (
    <div
      {...props}
      className={clsx(styles.cardFrame, props.className)}
      ref={ref}
    >
      {props.children}
    </div>
  )
})
PostCardFrame.displayName = 'PostCardFrame'

export const PostCardBody = ({
  showNotes = false,
  tags = <PostCardTags />,
}: {
  showNotes?: boolean
  /** The tags region rendered alongside `PostCardAddons`. */
  tags?: React.ReactNode
}): JSX.Element => {
  const post = usePostData()
  const title = useMetadataTitle(post?.metadata)
  const { showPanel, showLegacyNotes } = useCollaborationNotesPanel()
  const rawAnnotations = readFragment(
    PostCardContent_Annotation,
    post?.metadata?.annotations ?? [],
  )

  // Fix legacy posts where buff.ly links might not have https:// protocol
  const annotations = rawAnnotations.map((annotation) =>
    Object.assign({}, annotation, { url: ensureUrlProtocol(annotation.url) }),
  )

  return (
    <section className={styles.body}>
      {showNotes && showLegacyNotes && <PostCardNotes />}
      {showNotes && showPanel && (
        <PostNotesEntryPoint
          openInPopover
          variant="secondary"
          size="medium"
          className={styles.notesTrigger}
        />
      )}
      <div className={styles.bodyWrapper}>
        <PostCardHeader type={post.metadata?.type} />
        <PostCardContent
          title={title}
          text={post.text}
          annotations={annotations}
        />
        <div className={styles.mediaRow}>
          <PostMediaRowPreview assets={post.assets} />
        </div>
        <PostCardAttachments />
        <TagsAndAddons tags={tags} />
      </div>
      <div className={styles.mediaBentoBox}>
        <PostMediaBentoBoxPreview assets={post.assets} />
      </div>
    </section>
  )
}

const TagsAndAddons = ({ tags }: { tags: React.ReactNode }): JSX.Element => (
  <Flex justify="between" align="end" className={styles.tagsAddonsContainer}>
    {tags}
    <PostCardAddons />
  </Flex>
)

export const PostCard = forwardRef<
  HTMLDivElement,
  {
    post: FragmentOf<typeof PostCard_Post>
    className?: string
    borderless?: boolean
    rounded?: 'sm' | 'md' | 'lg'
    showChannelNotices?: boolean
    draggable?: boolean
    showNotes?: boolean
    footerLayout?: PostCardFooterLayout
  }
>(
  (
    {
      post,
      className,
      borderless,
      rounded = 'sm',
      showChannelNotices = false,
      showNotes = true,
      draggable = false,
      footerLayout,
    },
    ref,
  ) => {
    return (
      <PostCardProvider post={post}>
        <PostCardContainer
          className={clsx(
            styles[`rounded-${rounded}`],
            draggable && styles.draggable,
            className,
          )}
        >
          <PostCardFrame ref={ref}>
            <div
              className={clsx(styles.inner, {
                [styles.borderless]: borderless,
              })}
            >
              <PostCardErrorNotice
                showRefreshConnectionAction={showChannelNotices}
              />
              <PostCardProcessingNotice />
              <PostCardRecurrenceNotice />
              {showChannelNotices && <PostCardPausedQueueNotice />}
              {showChannelNotices && <PostCardDisconnectedChannelNotice />}
              <PostCardBody showNotes={showNotes} />
              <PostCardMetrics />
              <RefreshConnection />
              <PostCardFooter layout={footerLayout} />
            </div>
          </PostCardFrame>
        </PostCardContainer>
      </PostCardProvider>
    )
  },
)
PostCard.displayName = 'PostCard'

export const PostCardCompact = forwardRef<
  HTMLDivElement,
  { post: FragmentOf<typeof PostCard_Post>; className?: string }
>(({ post, className }, ref) => (
  <PostCardProvider post={post}>
    <PostCardContainer>
      <PostCardFrame ref={ref} className={className}>
        <div className={styles.inner}>
          <PostCardBody />
        </div>
      </PostCardFrame>
    </PostCardContainer>
  </PostCardProvider>
))
PostCardCompact.displayName = 'PostCardCompact'

export const PostCardSkeleton = forwardRef<
  HTMLDivElement,
  { className?: string }
>(({ className }, ref) => (
  <div ref={ref} className={clsx(styles.skeleton, className)}>
    <div className={styles.body}>
      <div className={styles.bodyWrapper}>
        <Flex gap="sm" align="center">
          <Skeleton rounded width={36} height={36} />
          <SkeletonText lines={1} width={120} />
        </Flex>
        <SkeletonText lines={3} width={'100%'} />
        <Flex className={styles.skeletonTags} gap="sm" align="center">
          <SkeletonText lines={1} width={80} />
          <SkeletonText lines={1} width={60} />
        </Flex>
      </div>
      <Skeleton className={styles.skeletonMedia} />
    </div>
  </div>
))

PostCardSkeleton.displayName = 'PostCardSkeleton'
